import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Receipt, Landmark, ClipboardCheck, Scale,
  Building2, ShieldCheck, Briefcase, ChevronRight
} from "lucide-react";
import axios from "axios";

const ICON_MAP = {
  Receipt, Landmark, ClipboardCheck, Scale,
  Building2, ShieldCheck, Briefcase,
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  const [services, setServices] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`).then((res) => {
      setServices(res.data.services);
    }).catch(() => {});
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#0A0D14] relative"
    >
      {/* Subtle top line */}
      <div className="gold-line w-24 mx-auto mb-16" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="font-work text-sm font-semibold tracking-[0.2em] text-[#D4AF37] uppercase mb-4">
            What We Offer
          </p>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
            Our Expertise
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => {
            const IconComp = ICON_MAP[service.icon] || Briefcase;
            const isActive = activeId === service.id;

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                data-testid={`service-card-${service.id}`}
                onClick={() => setActiveId(isActive ? null : service.id)}
                className="service-card group cursor-pointer bg-[#131821] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/30 hover:shadow-[0_8px_32px_rgba(212,175,55,0.08)]"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
                    <IconComp size={22} className="text-[#D4AF37]" />
                  </div>
                  <ChevronRight
                    size={18}
                    className={`text-[#94A3B8] transition-transform duration-300 ${
                      isActive ? "rotate-90" : "group-hover:translate-x-1"
                    }`}
                  />
                </div>

                <h3 className="font-outfit text-xl font-medium text-white mb-2">
                  {service.title}
                </h3>
                <p className="font-work text-sm text-[#94A3B8] leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Gold line on hover */}
                <div className="service-line h-[1px] bg-[#D4AF37]/40 mb-4" />

                {/* Sub-services (expandable) */}
                {isActive && (
                  <motion.ul
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-2 mt-2"
                  >
                    {service.sub_services.map((sub, j) => (
                      <li
                        key={j}
                        data-testid={`sub-service-${service.id}-${j}`}
                        className="flex items-start gap-2 text-sm text-[#94A3B8] font-work"
                      >
                        <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                        {sub}
                      </li>
                    ))}
                  </motion.ul>
                )}

                {!isActive && (
                  <p className="text-xs text-[#D4AF37]/60 font-work">
                    {service.sub_services.length} services — click to expand
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
